'use client';

import { Linkedin, MessageCircle, Share2, Twitter } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';

interface ShareLinksProps {
  textTemplate: string;
  salary: string;
}

function buildShareLinks(textTemplate: string, salary: string, pageUrl: string) {
  const pageUrlEncoded = encodeURIComponent(pageUrl);
  const shareText = encodeURIComponent(textTemplate.replace('{salary}', salary));

  return {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${pageUrlEncoded}`,
    whatsapp: `https://api.whatsapp.com/send?text=${shareText} ${pageUrlEncoded}`,
    twitter: `https://twitter.com/intent/tweet?text=${shareText}&url=${pageUrlEncoded}`,
  };
}

export function ShareLinks({ textTemplate, salary }: ShareLinksProps) {
  const [links, setLinks] = useState<{ linkedin: string; whatsapp: string; twitter: string } | null>(
    null,
  );

  useEffect(() => {
    // window.location is only available on the client; a synchronous setState is
    // acceptable here because it simply initializes data from the environment.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLinks(buildShareLinks(textTemplate, salary, window.location.href));
  }, [textTemplate, salary]);

  return (
    <div className="rounded-2xl border border-teal-100 bg-gradient-to-br from-[#f0fdfa] to-white p-6 text-center shadow-sm md:p-8">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#3bbdad]/10 text-[#3bbdad]">
        <Share2 className="h-6 w-6" />
      </div>
      <h3 className="mb-2 text-lg font-bold text-slate-900 md:text-xl">
        Is this data accurate? Ask your network!
      </h3>
      <p className="mb-6 text-slate-600">
        The best way to know market rates is to ask. Share this insight and start a poll.
      </p>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button
          asChild
          className="h-auto gap-2 rounded-full bg-[#0a66c2] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-[#0a66c2]/90"
        >
          <a href={links?.linkedin || '#'} target="_blank" rel="noopener noreferrer">
            <Linkedin className="h-4 w-4" />
            Poll on LinkedIn
          </a>
        </Button>
        <Button
          asChild
          className="h-auto gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-[#25D366]/90"
        >
          <a href={links?.whatsapp || '#'} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="h-4 w-4" />
            Ask via WhatsApp
          </a>
        </Button>
        <Button
          asChild
          className="h-auto gap-2 rounded-full bg-[#0f1419] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-[#0f1419]/90"
        >
          <a href={links?.twitter || '#'} target="_blank" rel="noopener noreferrer">
            <Twitter className="h-4 w-4" />
            Debate on X
          </a>
        </Button>
      </div>
    </div>
  );
}
